import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@store/index';

/** Client-wide request timeout: how long any call waits unless it says otherwise. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/**
 * REV-973: per-request timeout for every call that goes through revenant-api's
 * AI proxy (fix suggestions, criterion narratives).
 *
 * COUPLED to revenant-api's AI_PROVIDER_TIMEOUT_MS (src/utils/upstreamTimeout.js,
 * default 60000, REV-962). The API gives a hung provider that long and then
 * answers 504 with its own message. This must stay LONGER than that deadline,
 * or the client gives up first and the user sees "timeout of …ms exceeded"
 * instead of the API's explanation. If an operator raises
 * AI_PROVIDER_TIMEOUT_MS above this, raise this too.
 */
export const AI_REQUEST_TIMEOUT_MS = 75000;

// Create axios instance with defaults
const api = axios.create({
  baseURL: '/api',
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    // REV-802: the CSRF header revenant-api requires on its cookie-held auth
    // routes. Sent on every request so it is never forgotten on one of them;
    // it is harmless elsewhere.
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// REV-802: the access token lives in this module's memory, never in
// localStorage, where any script on the page could read it. It is short-lived;
// the session itself is two HttpOnly cookies scoped to /api/v1/auth, which
// JavaScript cannot read at all. A page reload starts with no token, and the
// first request's 401 renews it through /v1/auth/refresh.
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;

/** Set when a session ends on its own, so the login page can say why. */
export const SESSION_EXPIRED_KEY = 'sessionExpired';

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

/**
 * Delete the access token an earlier version kept in localStorage. It lived
 * for 24 hours and carried the user's email, and nothing reads it any more,
 * so without this nothing would ever remove it. Called once at start-up.
 */
export function forgetLegacyStoredToken(): void {
  try {
    localStorage.removeItem('accessToken');
  } catch {
    // localStorage can throw in private-mode/sandboxed contexts.
  }
}

/**
 * Get a new access token from the refresh cookie. Concurrent callers share one
 * request: the refresh token rotates on every use, so a second parallel
 * refresh would present a cookie the first had already spent.
 *
 * One 401 is retried. Another tab may have rotated the cookie between this
 * request leaving and arriving; by the time the 401 comes back the browser
 * holds the new cookie, and a second attempt succeeds.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await api.post<{ data?: { accessToken?: string } }>('/v1/auth/refresh');
          const token = res.data?.data?.accessToken ?? null;
          setAccessToken(token);
          return token;
        } catch (error) {
          if ((error as AxiosError).response?.status !== 401) break;
        }
      }
      setAccessToken(null);
      return null;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Requests whose 401 means "wrong credentials" or "no session", not "token expired". */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add authorization header if token exists
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    // Auth endpoints (login, register, refresh, logout) handle their own 401s.
    const original = error.config as RetriableConfig | undefined;
    const requestUrl = original?.url || '';
    const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => requestUrl.includes(path));

    if (error.response?.status !== 401 || isAuthEndpoint) {
      return Promise.reject(error);
    }

    // Someone who never signed in has no session to renew or to lose, so a
    // 401 is just a failed request: no refresh, no redirect, and no claim that
    // a session ended (REV-802 follow-up).
    if (!useAuthStore.getState().isAuthenticated) {
      return Promise.reject(error);
    }

    // An expired access token: renew it once from the cookie and replay.
    if (original && !original._authRetried) {
      original._authRetried = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
    }

    // The session itself is over. Clear what is left of it and send the user
    // to login. This is a hard navigation, so router state can't carry the
    // return path — stash it instead, and let LoginPage resume the journey
    // afterwards (REV-521), and say why they are there (REV-802).
    setAccessToken(null);
    useAuthStore.getState().logout();

    // Already on the login page: navigating to it again is a hard reload,
    // which re-fires whatever request just failed. Measured on the production
    // build, a 401 from GET /auth/providers here reloaded /login forever, each
    // round spending two refresh attempts and clearing the notice it came to
    // show (REV-802 follow-up).
    if (window.location.pathname === '/login') {
      return Promise.reject(error);
    }
    try {
      const current = window.location.pathname + window.location.search;
      sessionStorage.setItem('postLoginRedirect', current);
      sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
    } catch {
      // sessionStorage can throw in private-mode/sandboxed contexts; the
      // redirect below must still happen.
    }
    window.location.href = '/login';

    return Promise.reject(error);
  }
);

export default api;

// Type-safe API methods
export const apiClient = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    api.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown) => api.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) => api.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) => api.delete<T>(url).then((res) => res.data),
};
